import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Chip, DataTable, EmptyState, Pane, PaneHead, PhoneCards, PhoneScreen,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PeriodRow } from './components/PeriodRow';
import { exportRows } from './lib/exportCsv';
import {
  amount,
  money,
  thisPeriod,
  useAccountsSummary,
  useReceivables,
  type Receivable,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';

/**
 * Receivables — `g_recv` (3). WHAT IS OWED TO THE CLINIC.
 *
 * AGEING IS THE WHOLE CONTENT. The same forty thousand is a different problem
 * at fifteen days than at a hundred and twenty, so every row carries its
 * oldest item's age and the buckets behind it, and the list is ordered by the
 * oldest money rather than the largest — the oldest is the least likely to
 * arrive, which is the only ordering somebody chasing it would choose.
 *
 * AN INSURER AGES FROM WHEN THE CLAIM WAS FILED, not from the visit: terms are
 * counted from the filing, and a claim sitting unfiled is a different failure
 * on a different screen. A patient's bill ages from its own date.
 *
 * PATIENTS ARE ONE ROW. A clinic chases a named insurer and chases patient
 * debt as a pile — the board says "debts 13,450 · insurers 41,200" — so
 * listing 165 individuals here would bury the four rows that can actually be
 * phoned.
 */

const NONE: Receivable[] = [];

export function ReceivablesPage() {
  const go = useNavigate();
  const [period, setPeriod] = useState(thisPeriod);
  const [pickedId, setPicked] = useState<string | null>(null);

  const { data, isLoading, isError } = useReceivables();
  const [query, setQuery] = useState('');
  const all = data ?? NONE;
  /* Insurers first — the clock that is running, then the patients' bills. */
  const rows = [...all]
    .sort((a, b) => (a.kind === b.kind ? b.owedMinor - a.owedMinor : a.kind === 'insurer' ? -1 : 1))
    .filter((r) => !query.trim() || r.name.toLowerCase().includes(query.trim().toLowerCase()));
  const { data: summary } = useAccountsSummary(period);

  usePageMeta({ title: 'Receivables' });

  const totals = useMemo(() => {
    const patients = all.filter((r) => r.kind === 'patient');
    const insurers = all.filter((r) => r.kind === 'insurer');
    return {
      patients: patients.reduce((s, r) => s + r.owedMinor, 0),
      insurers: insurers.reduce((s, r) => s + r.owedMinor, 0),
      /* PAST TERMS is the figure that decides the day: anything over sixty
         days is past every insurer's terms the clinic has, and past any
         patient's patience. */
      past: all.reduce((s, r) => s + r.d60 + r.d90, 0),
    };
  }, [all]);

  const picked = rows.find((r) => key(r) === pickedId) ?? null;


  const tableRows: TableRow[] = rows.map((r) => ({
    id: key(r),
    cells: {
      who: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">{r.name}</p>
          <p className="t-cap mut">
            {r.kind === 'insurer'
              ? `${r.items} claim${r.items === 1 ? '' : 's'} approved, unpaid`
              : `${r.items} bill${r.items === 1 ? '' : 's'} across every branch`}
          </p>
        </div>
      ),
      owed: <span className="t-strong num">{amount(r.owedMinor)}</span>,
      age: <AgeChip row={r} />,
      source: (
        <span className="t-sec mut">{r.kind === 'insurer' ? 'Claims' : 'Patient bills'}</span>
      ),
    },
  }));

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      /* `g_recv` — the desk table as cards, over the table's own rows, so a
         figure can never read differently here. */
      phone: (
        <PhoneScreen
          className="acc-phone"
          title="Receivables"
          sub="who owes, and how long"
          menu={(close) => <AccountantMenu door="receivables" onClose={close} />}
        >
          <PhoneCards
            columns={[
              { key: 'who', label: 'Who', width: 46 },
              { key: 'owed', label: 'Amount', width: 16 },
              { key: 'age', label: 'Age', width: 20 },
              { key: 'source', label: 'Source', width: 18 },
            ]}
            rows={tableRows}
            body="who"
            trailing="owed"
            hide={['"source"']}
            onOpen={(r) => setPicked(r.id)}
            empty={<EmptyState title="Nobody owes anything" sub="Every bill raised has been settled — the ageing is empty." />}
          />
        </PhoneScreen>
      ),
      title: 'Receivables',
      sub: (
        <>
          what is owed to the clinic <Chip kind="outline" small>read-only</Chip>
        </>
      ),
      search: 'Search an insurer, a patient, a claim…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        disabled: !rows.length,
        onClick: () =>
          exportRows(
            `receivables-${period}`,
            ['Who', 'Kind', 'Owed', 'Items', 'Oldest (days)', 'Under 30', '30–59', '60–89', '90+'],
            rows.map((r) => [
              r.name,
              r.kind,
              (r.owedMinor / 100).toFixed(2),
              String(r.items),
              String(r.oldestDays),
              (r.current / 100).toFixed(2),
              (r.d30 / 100).toFixed(2),
              (r.d60 / 100).toFixed(2),
              (r.d90 / 100).toFixed(2),
            ]),
          ),
      },
      pane: picked ? (
        <RowPane row={picked} onClaims={() => go('/accountant/bills?tab=claims')} onClose={() => setPicked(null)} />
      ) : (
        <Pane
          head={
            <PaneHead
              overline="Selected row"
              title="Nothing picked"
              sub="pick a row to see how the debt is aged"
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              The desk sends the statement; you read the clock. Only a recorded remittance clears a
              claim.
            </p>
          </div>
        </Pane>
      ),
    },
    [picked, rows.length, period, pickedId, query],
  );

  return (
    <div className="maie desk acc-root">
      <PeriodRow period={period} onChange={setPeriod} branches={summary?.branches} />

      <div className="dcontent scroll">
        <div className="acc-stats acc-stats-3">
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Patient debts</p>
            <p className="t-dxl mt8">{money(totals.patients)}</p>
          </div>
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Insurers approved-unpaid</p>
            <p className="t-dxl mt8">{money(totals.insurers)}</p>
          </div>
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Past terms</p>
            <p className={`t-dxl mt8${totals.past ? ' red' : ''}`}>{money(totals.past)}</p>
            <p className="t-cap mut mt4">sixty days and over</p>
          </div>
        </div>

        <DataTable
          caption="Who owes the clinic"
          columns={[
            { key: 'who', label: 'Who', width: 46 },
            { key: 'owed', label: 'Amount', width: 16 },
            { key: 'age', label: 'Age', width: 20 },
            { key: 'source', label: 'Source', width: 18 },
          ]}
          rows={tableRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Reading the ledger…</p>
              ) : isError ? (
                <p className="t-sec mut">
                  The ledger did not come back. Nothing has been changed.
                </p>
              ) : (
                <>
                  <p className="es-t">Nobody owes the clinic anything</p>
                  <p className="t-sec mut">
                    Every bill is settled and every filed claim has been remitted. This is the
                    screen you want to be empty.
                  </p>
                </>
              )}
            </div>
          }
        />

        <p className="t-sec mut mt16">
          An insurer ages from the day its claim was FILED, because that is when its terms start
          running. A patient’s bill ages from its own date.
        </p>
      </div>
    </div>
  );
}

const key = (r: Receivable) => `${r.kind}:${r.id ?? 'all'}`;

/** How long the oldest item has waited, coloured by whose terms it has passed. */
function AgeChip({ row }: { row: Receivable }) {
  const past = row.d60 + row.d90;
  return (
    <span>
      {past > 0 ? (
        <Chip kind="danger" small>
          {row.oldestDays} d · {amount(past)} past terms
        </Chip>
      ) : row.d30 > 0 ? (
        <Chip kind="warn" small>{row.oldestDays} d</Chip>
      ) : (
        <span className="t-sec mut">{row.oldestDays} d · within terms</span>
      )}
    </span>
  );
}

function RowPane({
  row,
  onClaims,
  onClose,
}: {
  row: Receivable;
  onClaims: () => void;
  onClose: () => void;
}) {
  const buckets: Array<[string, number]> = [
    ['Under 30 days', row.current],
    ['30 – 59 days', row.d30],
    ['60 – 89 days', row.d60],
    ['90 days and over', row.d90],
  ];

  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Selected row"
          title={`${row.name} — statement`}
          sub={row.kind === 'insurer' ? 'approved, unpaid' : 'billed, unpaid'}
          wrapTitle
        />
      }
      foot={
        row.kind === 'insurer' ? (
          <button type="button" className="btn btn-outline grow" style={{ minHeight: 44 }} onClick={onClaims}>
            Open the claims list
          </button>
        ) : undefined
      }
    >
      <Card>
        <p className="t-ovl mut">{row.kind === 'insurer' ? 'Approved, unpaid' : 'Billed, unpaid'}</p>
        <p className="t-sec sec mt4">
          {row.items} {row.kind === 'insurer' ? 'claim' : 'bill'}
          {row.items === 1 ? '' : 's'} · {amount(row.owedMinor)}
        </p>
        <p className="t-cap mut mt8">
          the oldest has been waiting {row.oldestDays} day{row.oldestDays === 1 ? '' : 's'}
        </p>
      </Card>

      <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>How it ages</p>
      {buckets.map(([label, value]) => (
        <Card key={label} style={value && label.startsWith('90') ? { borderColor: 'var(--danger-600)' } : undefined}>
          <div className="flex">
            <p className="t-sec sec grow">{label}</p>
            <p className={`t-strong num${value && (label.startsWith('90') || label.startsWith('60')) ? ' red' : ''}`}>
              {value ? amount(value) : '—'}
            </p>
          </div>
        </Card>
      ))}

      <Card kind="accent">
        <p className="t-sec brand">
          {row.kind === 'insurer'
            ? 'The desk sends the statement; you read the clock. Only a recorded remittance clears it.'
            : 'Patient debt is chased at the desk, one call at a time. Nothing here writes it off.'}
        </p>
      </Card>
    </Pane>
  );
}
